import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { readableBehavior as readable } from 'utils/behaviorInterval';

/**
 * OTHER BEHAVIOURS SEEN DURING A BEHAVIOUR-INTERVAL RECORDING (read-only).
 * Reads a behavior-interval session / raw_data: `otherBehaviors` (one array of
 * behaviour values per interval), `otherBehaviorLabels` ({ value: name }) and,
 * for records without the per-interval list, `otherBehaviorCounts`.
 * Shows one chip per behaviour - "Crying · 2" - with the interval numbers in its
 * tooltip. Renders nothing for records saved before the feature existed.
 */
const OtherBehaviorsSummary = ({ record, labelOf, sx }) => {
  const intl = useIntl();
  if (!record) return null;
  const labels = record.otherBehaviorLabels || {};
  const nameOf = (v) => (labelOf && labelOf(v) !== v ? labelOf(v) : labels[v] || readable(v) || v);

  const intervals = {};
  if (Array.isArray(record.otherBehaviors)) {
    record.otherBehaviors.forEach((cell, i) =>
      (Array.isArray(cell) ? cell : []).forEach((v) => {
        if (!v || v === record.behavior) return;
        (intervals[v] = intervals[v] || []).push(i + 1);
      })
    );
  } else if (record.otherBehaviorCounts) {
    Object.entries(record.otherBehaviorCounts).forEach(([v, n]) => {
      if (v !== record.behavior) intervals[v] = new Array(Number(n) || 0).fill(null);
    });
  }
  const entries = Object.entries(intervals).filter(([, list]) => list.length > 0);
  if (entries.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={sx}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {intl.formatMessage({ id: 'form.behaviorInterval.otherBehaviorsSeen', defaultMessage: 'Other behaviours seen:' })}
      </Typography>
      {entries.map(([v, list]) => {
        const nums = list.filter((n) => n !== null);
        return (
          <Tooltip
            key={v}
            title={
              nums.length
                ? intl.formatMessage(
                    { id: 'form.behaviorInterval.otherInIntervals', defaultMessage: 'Intervals {list}' },
                    { list: nums.join(', ') }
                  )
                : ''
            }
          >
            <Chip size="small" variant="outlined" color="info" label={`${nameOf(v)} · ${list.length}`} />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

OtherBehaviorsSummary.propTypes = {
  record: PropTypes.object,
  labelOf: PropTypes.func,
  sx: PropTypes.object
};

export default OtherBehaviorsSummary;
